import {
  Check,
  Clock,
  Ban,
  AlertCircle,
  HelpCircle,
  Hourglass,
  Loader2,
  TimerOff,
} from "lucide-react";

import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { SigningItemStatus } from "../types";

/** Same shape as the document-state badge: soft fill, matching border + text. */
const STATUS_CLASSES: Record<SigningItemStatus, string> = {
  awaiting_you: "bg-primary/10 text-primary border-primary/20",
  // Muted on purpose: the row is in flight but there is nothing for the viewer
  // to do, so it must not compete with awaiting_you's call to action.
  awaiting_others: "bg-muted text-muted-foreground border-border",
  // Signed's colour at a lower weight: the signatures are real and this row is
  // one poll from "Signed", so it must not read as another kind of waiting.
  finalizing: "bg-success/5 text-success border-success/20",
  // The documents page's Verified green (VerifiedBadge) — one "done" colour.
  signed: "bg-success/10 text-success border-success/40",
  failed: "bg-red-100 text-red-700 border-red-200",
  cancelled: "bg-gray-100 text-gray-700 border-gray-200",
  expired: "bg-amber-100 text-amber-700 border-amber-200",
  unknown: "bg-muted text-muted-foreground border-border",
};

const STATUS_ICONS = {
  awaiting_you: Clock,
  awaiting_others: Hourglass,
  finalizing: Loader2,
  signed: Check,
  failed: AlertCircle,
  cancelled: Ban,
  expired: TimerOff,
  unknown: HelpCircle,
} as const;

export function SigningStatusBadge({
  status,
  className,
}: {
  status: SigningItemStatus;
  className?: string;
}) {
  const { t } = useLanguage();
  const Icon = STATUS_ICONS[status];
  const labels: Record<SigningItemStatus, string> = {
    awaiting_you: t.signing.status.awaitingYou,
    awaiting_others: t.signing.status.awaitingOthers,
    finalizing: t.signing.status.finalizing,
    signed: t.signing.status.signed,
    failed: t.signing.status.failed,
    cancelled: t.signing.status.cancelled,
    expired: t.signing.status.expired,
    unknown: t.signing.status.unknown,
  };

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold",
        STATUS_CLASSES[status],
        className
      )}
    >
      <Icon
        className={cn("h-3 w-3", status === "finalizing" && "animate-spin")}
        aria-hidden="true"
      />
      {labels[status]}
    </span>
  );
}
